import { useState, useCallback } from "react";
import axios from "axios";
import { useClient } from "ClientContext";
import { useCheckout } from "context/CheckoutContext";
import { useRewardfull } from "./useRewardfull";

interface fetchReservationProps {
  quoteId: string;
  ratePlanID: string;
  ccToken: string;
  guest: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
  policy?: {
    privacy: {
      dateOfAcceptance: Date;
      isAccepted: boolean;
      version: number;
    };
    termsAndConditions: {
      isAccepted: boolean;
      dateOfAcceptance: Date;
    };
    marketing: {
      isAccepted: boolean;
    };
  } | null;
}

export function useGuestyReservation() {
  const {reservation,setReservation, context_quote} = useCheckout();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { internal_ID,actions,referralId } = useClient();
  const { trackConversion, isUpdatingStripeCustomer, startStripeUpdate } = useRewardfull();
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  // Memoized fetchReservation function
  const handleFetchReservation = useCallback(
    async ({
      quoteId,
      ratePlanID,
      ccToken,
      guest,
      policy
    }: fetchReservationProps): Promise<void> => {
      setError(null);
      setLoading(true);
      
      // Start Stripe update loading state immediately if referral exists
      if (ccToken && referralId) {
        startStripeUpdate();
      }

      try {
        const response = await axios.post(`${SERVER_URL}/api/controller`, {
          action: actions.fetchListingReservation,
          internal_ID,
          params: {
            reservation: {
              quoteId,
              ratePlanID,
              ccToken,
              guest,
              policy
            },
          },
        });
        
        // Track conversion with Rewardfull after successful reservation
        // This will wait for Guesty to create customer then update it with referral data
        if (ccToken && referralId) {
          // trackConversion handles the 5-second wait internally
          await trackConversion({
            email: guest.email,
            firstName: guest.firstName,
            lastName: guest.lastName,
            externalId: response.data.confirmationCode ,
            saleAmount: 0,
            currency: context_quote?.currency
          });
        }
        
        // Only set reservation after Stripe update is complete (or immediately if no referralId)
        setReservation(response.data);
      } catch (err) {
        setError("Error fetching data. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [ trackConversion, context_quote, referralId, startStripeUpdate] // Dependencies for memoization
  );


  return {
    loading,
    error,
    handleFetchReservation,
    reservation,
    isUpdatingStripeCustomer  // Pass loading state to consumers
  };
}
